import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Modal, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  useMediaQuery, 
  useTheme, 
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Avatar,
  Divider,
  Grid,
  Chip,
  InputAdornment
} from '@mui/material';
import { fetchProducts, saveClientWithProductsUpdate, fetchCodes, markCodeAsUsed, addCode, uploadCheckImage, fetchAddresses } from '../api/github';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ReviewsIcon from '@mui/icons-material/Reviews';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  width: '95%',
  maxWidth: 600,
  p: 2,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const formatMoney = (value) => {
  if (!value) return '';
  const num = value.toString().replace(/\D/g, '');
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export default function AddClientModal({ open, onClose, employeeId }) {
  const [nickname, setNickname] = useState('');
  const [code, setCode] = useState('');
  const [codeInputType, setCodeInputType] = useState('select');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 16));
  const [prepayments, setPrepayments] = useState([{ amount: '', date: new Date().toISOString().slice(0, 16), comment: '' }]);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [actualDeliveryTime, setActualDeliveryTime] = useState('');
  const [clientReview, setClientReview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codes, setCodes] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductList, setShowProductList] = useState(false);
  const [showCodeList, setShowCodeList] = useState(false);
  const [codeSearch, setCodeSearch] = useState('');
  const [partyDate, setPartyDate] = useState('');
  const [partyComment, setPartyComment] = useState('');
  const [checkImages, setCheckImages] = useState([]);
  const [showAddressList, setShowAddressList] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');
  const [addresses, setAddresses] = useState([]);

  const productSearchRef = useRef(null);
  const codeSearchRef = useRef(null);
  const addressSearchRef = useRef(null);

  const removeProduct = (index) => {
    const updatedProducts = [...selectedProducts];
    updatedProducts.splice(index, 1);
    setSelectedProducts(updatedProducts);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [productsData, codesData, addressesData] = await Promise.all([
          fetchProducts(),
          fetchCodes(false),
          fetchAddresses()
        ]);
        setProducts(productsData || []);
        setCodes(codesData.reverse() || []);
        setAddresses(addressesData || []);
      } catch (error) {
        console.error("Ошибка загрузки данных:", error);
        setProducts([]);
        setCodes([]);
        setAddresses([]);
      }
    };
    
    if (open) {
      loadData();
      setPrepayments([{ amount: '', date: new Date().toISOString().slice(0, 16), comment: '' }]);
      setDeliveryAddress('');
      setDeliveryTime('');
      setNickname('');
      setActualDeliveryTime('');
      setClientReview('');
      setPartyDate('');
      setPartyComment('');
      setCheckImages([]);
      setSelectedProducts([]);
      setCode('');
      setProducts([]);
      setCodes([]);
      setCodeSearch('');
      setAddressSearch('');
    }
  }, [open]);

  const handleCheckUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    try {
      setIsSubmitting(true);
      const uploadedUrls = await Promise.all(
        files.map(file => uploadCheckImage(file))
      );

      const newChecks = uploadedUrls.map(url => ({
        id: `check_${Date.now()}_${Math.random()}`,
        url,
        isPaid: true,
        paidDate: null
      }));

      setCheckImages([...checkImages, ...newChecks]);
    } catch (error) {
      console.error("Чекларни юклашда хатолик:", error);
      alert(error.message || 'Чекларни юклашда хатолик юз берди');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);

      let codeToSave = code;
      let isNewCode = false;

      const allCodes = await fetchCodes(true);
      const existingCode = allCodes.find(c => c.value === code);

      if (codeInputType === 'select' && existingCode) {
        await markCodeAsUsed(existingCode.id);
      }
      
      if (codeInputType === 'manual') {
        await addCode({
          value: code,
          description: `Мижоз учун яратилган код ${nickname}`,
          isUsed: true,
          createdAt: new Date().toISOString(),
          usedAt: new Date().toISOString()
        });
      }

      if (!nickname.trim() || !code.trim()) {
        throw new Error('Хамма тўдириш жойларини тўдиринг');
      }

      if (selectedProducts.length === 0) {
        throw new Error('Минимум битта товар қўшишиз шарт');
      }

      const enrichedProducts = selectedProducts.map(item => {
        const product = products.find(p => p.id === item.productId);
        if (!product) throw new Error(`Махсулот IDси ${item.productId} билан топилмади`);
        if (product.stock < item.quantity) {
          throw new Error(`Махсулот "${product.name}" омборда йетарли эмас`);
        }

        return {
          ...item,
          productName: product.name,
          productPrice: product.price,
          productImage: product.image,
          totalPrice: product.price * item.quantity,
        };
      });

      const totalAmount = enrichedProducts.reduce((sum, item) => sum + item.totalPrice, 0);
      const paidAmount = prepayments.reduce((sum, payment) => sum + (parseFloat(payment.amount.replace(/\s/g, '')) || 0), 0);

      const clientData = {
        id: `client_${Date.now()}`,
        nickname,
        code,
        orders: [{
          id: `order_${Date.now()}`,
          checkImages,
          date: orderDate,
          products: enrichedProducts,
          totalAmount,
          employeeId,
          employeeName: localStorage.getItem('userName'),
          paymentInfo: {
            prepayments: prepayments.filter(p => p.amount).map(p => ({
              ...p,
              amount: parseFloat(p.amount.replace(/\s/g, '')) || 0,
              comment: p.comment || ''
            })),
            history: [],
            paidAmount,
          },
          deliveryInfo: {
            address: deliveryAddress,
            scheduledTime: deliveryTime,
            actualTime: actualDeliveryTime
          },
          partyDate: partyDate,
          partyComment: partyComment,
          review: clientReview, 
          status: paidAmount >= totalAmount ? 'Тўллиқ тўланган' : 
                 paidAmount > 0 ? 'Охиргача тўланмаган' : 'Тўланмаган'
        }],
        totalSpent: totalAmount,
        firstOrderDate: orderDate,
        lastOrderDate: orderDate
      };

      const productsUpdate = selectedProducts.map(item => ({
        productId: item.productId,
        quantity: item.quantity
      }));

      await saveClientWithProductsUpdate(clientData, productsUpdate);

      onClose(true);
    } catch (error) {
      console.error("Сақлашда хатолик:", error);
      alert(error.message || "Маълумотларни сақлаш мумкин бўлмади");
    } finally {
      setIsSubmitting(false);
    }
  };

  const addProduct = () => {
    setSelectedProducts([...selectedProducts, { productId: '', quantity: 1 }]);
  };

  const updateProduct = (index, field, value) => {
    const updated = [...selectedProducts];
    updated[index][field] = field === 'quantity' ? Math.max(1, parseInt(value) || 1) : value;
    setSelectedProducts(updated);
  };

  const addPrepayment = () => {
    setPrepayments([...prepayments, { amount: '', date: new Date().toISOString().slice(0, 16), comment: '' }]);
  };

  const removePrepayment = (index) => {
    if (prepayments.length <= 1) return;
    const updated = [...prepayments];
    updated.splice(index, 1);
    setPrepayments(updated);
  };

  const updatePrepayment = (index, field, value) => {
    const updated = [...prepayments];
    updated[index][field] = value;
    setPrepayments(updated);
  updated[field] = value;
    setPrepayments(updated);
  };

  const handleProductSearchChange = useCallback((e) => {
    setProductSearch(e.target.value);
  }, []);

  const handleCodeSearchChange = (e) => {
    setCodeSearch(e.target.value);
  };

  const handleAddressSearchChange = useCallback((e) => {
    setAddressSearch(e.target.value);
  }, []);

  const ProductSelectionModal = () => {
    useEffect(() => {
      if (showProductList && productSearchRef.current) {
        productSearchRef.current.focus();
      }
    }, [showProductList]);

    return (
      <Modal open={showProductList} disableAutoFocus onClose={() => setShowProductList(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '95%' : '80%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
        disableAutoFocus
        >
          <Typography variant="h6" gutterBottom>Махсулот танлаш</Typography>
          
          <TextField
            inputRef={productSearchRef}
            label="Қидириш"
            fullWidth
            autoFocus
            margin="normal"
            value={productSearch}
            onChange={handleProductSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {products
              .filter(product => 
                product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                product.description?.toLowerCase().includes(productSearch.toLowerCase())
              )
              .map(product => (
                <ListItem
                  key={product.id}
                  button
                  onClick={() => {
                    setSelectedProducts([...selectedProducts, {
                      productId: product.id,
                      quantity: '1'
                    }]);
                    setShowProductList(false);
                  }}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    display: 'flexDirection',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid rgba(0,0,0,0.1)'
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={product.image} 
                      alt={product.name}
                      variant="square"
                      sx={{ width: 56, height: 56, mr: 2 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={product.name}
                    secondary={
                      <>
                        <Typography component="span" display="block">
                          {formatMoney(product.price)} Сумм
                        </Typography>
                        {product.description && (
                          <Typography component="span" variant="span" tag="variant" variant="body2" color="text.secondary">
                          {product.description}
                          </Typography>
                          )}
                      </>
                    }
                    />
                  <Chip 
                    label="Chip" 
                    color="success"
                    label={`${formatMoney(product.stock)} дона`} 
                    size="small"
                    sx={{ ml: isMobile ? '0' : '2', mt: isMobile ? '1' : '0' }}
                    color={product.stock > 0 ? 'success' : 'error'}
                  />
                </ListItem>
              ))}

            {products.filter(product => 
              product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
              product.description?.toLowerCase().includes(productSearch.toLowerCase())
            ).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                Махсулотлар топилмади
              </Typography>
            )}

          </List>
        </Box>
      </Modal>
    );
  };

  const CodeSelectionModal = () => {
    useEffect(() => {
      if (showCodeList && codeSearchRef.current) {
        codeSearchRef.current.focus();
      }
    }, [showCodeList]);

    return (
      <Modal open={showCodeList} disableAutoFocus onClose={() => setShowCodeList(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '95%' : '80%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
        disableAutoFocus
        >
          <Typography variant="h6" gutterBottom>Код танлаш</Typography>
          
          <TextField
            inputRef={codeSearchRef}
            label="Код қидириш"
            fullWidth
            autoFocus
            margin="normal"
            value={codeSearch}
            onChange={handleCodeSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {codes
              .filter(codeItem => 
                codeItem.value.toLowerCase().includes(codeSearch.toLowerCase()) ||
                codeItem.description.toLowerCase().includes(codeSearch.toLowerCase())
              )
              .map(codeItem => (
                <ListItem 
                  key={codeItem.id} 
                  button
                  onClick={() => {
                    setCode(codeItem.value);
                    setShowCodeList(false);
                    setCodeSearch('');
                  }}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(0,0,0,0.1)'
                  }}
                >
                  <ListItemText
                    primary={codeItem.value}
                    secondary={codeItem.description}
                  />
                </ListItem>
              ))}
          </List>
          {codes.filter(codeItem => 
            codeItem.value.toLowerCase().includes(codeSearch.toLowerCase()) ||
            codeItem.description.toLowerCase().includes(codeSearch.toLowerCase())
          ).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              Кодлар топилмади
            </Typography>
          )}
        </Box>
      </Modal>
    );
  };

  const AddressSelectionModal = () => {
    useEffect(() => {
      if (showAddressList && addressSearchRef.current) {
        addressSearchRef.current.focus();
      }
    }, [showAddressList]);

    return (
      <Modal open={showAddressList} disableAutoFocus onClose={() => setShowAddressList(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '95%' : '80%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <Typography variant="h6" gutterBottom>Адрес танлаш</Typography>
          
          <TextField
            inputRef={addressSearchRef}
            label="Адрес қидириш"
            fullWidth
            autoFocus
            margin="normal"
            value={addressSearch}
            onChange={handleAddressSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {addresses
              .filter(addr => addr.address.toLowerCase().includes(addressSearch.toLowerCase()))
              .map(addr => (
                <ListItem 
                  key={addr.id} 
                  button
                  onClick={() => {
                    setDeliveryAddress(addr.address);
                    setShowAddressList(false);
                    setAddressSearch('');
                  }}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(0,0,0,0.1)'
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <LocationOnIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={addr.address}
                    secondary={`Қўшилган: ${new Date(addr.createdAt).toLocaleDateString('uz-UZ')}`}
                  />
                </ListItem>
              ))}
          </List>
          {addresses.filter(addr => addr.address.toLowerCase().includes(addressSearch.toLowerCase())).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              Адреслар топилмади
            </Typography>
          )}
        </Box>
      </Modal>
    );
  };

  const totalAmount = selectedProducts.reduce((sum, item) => {
    const product = products.find(p => p.id === item.productId);
    return sum + (product ? product.price * item.quantity : 0);
  }, 0);

  const paidAmount = prepayments.reduce((sum, payment) => sum + (parseFloat(payment.amount.replace(/\s/g, '')) || 0), 0);
  const remainingAmount = totalAmount - paidAmount;
  const overpaidAmount = paidAmount > totalAmount ? paidAmount - totalAmount : 0;

  return (
    <Modal open={open} onClose={() => !isSubmitting && onClose(false)}>
      <Box sx={style}>
        <Typography variant="h6" sx={{ mb: 2 }}>Мижоз қўшиш</Typography>
        
        <Grid >
          <Grid item xs={12}>
            <TextField
              label="Мижозни ники"
              fullWidth
              margin="normal"
              size="small"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              disabled={isSubmitting}
              required
            />
          </Grid>

          <Divider sx={{ my: 2 }} />
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant={codeInputType === 'select' ? 'contained' : 'outlined'}
                onClick={() => setCodeInputType('select')}
                size="small"
                fullWidth
              >
                Кодни танлаш
              </Button>
              <Button
                variant={codeInputType === 'manual' ? 'contained' : 'outlined'}
                onClick={() => setCodeInputType('manual')}
                size="small"
                fullWidth
              >
                Кодни киритиш
              </Button>
            </Box>
          </Grid>
          
          {codeInputType === 'select' ? (
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" size="small" required>
                <Select
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  label="Мижознинг коди"
                  disabled={codes.length === 0}
                >
                  <MenuItem value="">
                    <em>Кодни танланг</em>
                  </MenuItem>
                  {codes.map(codeItem => (
                    <MenuItem 
                      key={codeItem.id} 
                      value={codeItem.value}
                    >
                      {codeItem.value} - {codeItem.description}
                    </MenuItem>
                  ))}
                </Select>
                {codes.length === 0 && (
                  <Typography variant="caption" color="error">
                    Барча кодлар ишлатилган. Янги код қўшинг ёки қолдиқ кутувдаги кодни киритинг.
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => {
                    setShowCodeList(true);
                    setCodeSearch('');
                  }}
                  disabled={isSubmitting || codes.length === 0}
                  sx={{ mt: 1 }}
                  fullWidth
                  size="small"
                >
                  Код қидириш
                </Button>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TextField
                label="Мижознинг коди"
                fullWidth
                margin="normal"
                size="small"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Grid>
          )}
          
          <Grid item xs={12}>
            <TextField
              label="Буйуртма сана ва вақти"
              type="datetime-local"
              fullWidth
              margin="normal"
              size="small"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1" sx={{ mt: 2 }}>Махсулотлар:</Typography>

        {selectedProducts.map((item, index) => {
          const product = products.find(p => p.id === item.productId);
          return (
            <Box key={index} sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row', 
              gap: 1, 
              mt: 1,
              alignItems: isMobile ? 'flex-start' : 'center',
              position: 'relative',
              border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: 1,
              p: 1
            }}>
              <IconButton
                onClick={() => removeProduct(index)}
                sx={{
                  marginLeft: isMobile ? '45%' : 0,
                  padding: 0.5,
                  color: 'error.main'
                }}
                disabled={isSubmitting}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              
              <FormControl sx={{ mb: isMobile ? 1 : 0, width: isMobile ? '100%' : '60%' }} size="small" disabled={isSubmitting}>
                <Select
                  value={item.productId}
                  onChange={(e) => updateProduct(index, 'productId', e.target.value)}
                  label="Махсулот"
                  renderValue={(selected) => {
                    const product = products.find(p => p.id === selected);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {product?.image && (
                          <Avatar 
                            src={product.image} 
                            alt={product.name}
                            variant="square"
                            sx={{ width: 40, height: 40, mr: 1 }}
                          />
                        )}
                        <Typography variant="body2">
                          {product ? product.name : 'Махсулот танланг'}
                        </Typography>
                      </Box>
                    );
                  }}
                >
                  {products.map(product => (
                    <MenuItem 
                      key={product.id} 
                      value={product.id}
                      disabled={product.stock <= 0}
                      sx={{ py: 0.5 }}
                    >
                      <Avatar 
                        src={product.image} 
                        alt={product.name}
                        variant="square"
                        sx={{ width: 40, height: 40, mr: 1 }}
                      />
                      <Box>
                        <Typography variant="body2">{product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.stock} дона • {formatMoney(product.price)} Сумм
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
                <IconButton 
                  onClick={() => updateProduct(index, 'quantity', Math.max(1, item.quantity - 1))}
                  disabled={isSubmitting || item.quantity <= 1}
                  size="small"
                  sx={{background: 'green', color: 'black', padding: 0.6, paddingLeft: 1, paddingRight: 1}}
                >
                  -
                </IconButton>
                <TextField
                  label="Миқдор"
                  type="number"
                  value={item.quantity}
                  onChange={(e) => updateProduct(index, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{ width: isMobile ? '50%' : 70 }}
                  disabled={isSubmitting}
                  size="small"
                />
                <IconButton 
                  onClick={() => {
                    const product = products.find(p => p.id === item.productId);
                    if (product && item.quantity < product.stock) {
                      updateProduct(index, 'quantity', parseFloat(item.quantity) + 1);
                    }
                  }}
                  disabled={isSubmitting || 
                    (products.find(p => p.id === item.productId)?.stock <= item.quantity)}
                  size="small"
                  sx={{background: 'green', color: 'black', padding: 0.6, paddingLeft: 1, paddingRight: 1}}
                >
                  +
                </IconButton>

                {product && (
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    = {formatMoney(product.price * item.quantity)} Сумм
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}

        <Button 
          onClick={() => {
            setShowProductList(true);
            setProductSearch('');
          }} 
          sx={{ mt: 1 }}
          disabled={isSubmitting}
          startIcon={<AddIcon />}
          size="small"
          fullWidth
          variant="outlined"
        >
          Махсулот қўшиш
        </Button>

        <ProductSelectionModal />
        <CodeSelectionModal />
        <AddressSelectionModal />

        <Divider sx={{ my: 2 }} />
        
        {/* Секция оплаты */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PaymentIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1">Тўлов маълумотлари</Typography>
        </Box>
        
        {prepayments.map((payment, index) => (
          <Box key={index} sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row', 
            gap: 1, 
            mb: 1,
            alignItems: isMobile ? 'flex-start' : 'center'
          }}>
            <TextField
              label="Тўлов суммаси"
              type="text"
              fullWidth
              margin="normal"
              size="small"
              value={formatMoney(payment.amount)}
              onChange={(e) => updatePrepayment(index, 'amount', e.target.value.replace(/\s/g, ''))}
              disabled={isSubmitting}
              InputProps={{
                endAdornment: <InputAdornment position="end">Сумм</InputAdornment>,
              }}
            />
            
            <TextField
              label="Сана"
              type="datetime-local"
              fullWidth
              margin="normal"
              size="small"
              value={payment.date}
              onChange={(e) => updatePrepayment(index, 'date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting}
            />
            
            <TextField
              label="Комментарий"
              fullWidth
              margin="normal"
              size="small"
              value={payment.comment}
              onChange={(e) => updatePrepayment(index, 'comment', e.target.value)}
              disabled={isSubmitting}
            />
            
            {prepayments.length > 1 && (
              <IconButton 
                onClick={() => removePrepayment(index)}
                disabled={isSubmitting}
                color="error"
                sx={{ mt: isMobile ? 0 : 1, border: '1px solid red', borderRadius: '3px', width: '100%' }}
                fullWidth
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        
        <Button 
          onClick={addPrepayment}
          sx={{ mt: 1 }}
          disabled={isSubmitting}
          startIcon={<AddIcon />} 
          size="small"
          fullWidth
          variant="outlined"
        >
          Тўлов қўшиш
        </Button>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1, 
          mt: 2,
          p: 1,
          backgroundColor: '#f5f5f5',
          borderRadius: 1
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#460101' }}>
            <strong>Махсулот:</strong> {formatMoney(totalAmount ? totalAmount : "0") } Сумм
          </Typography>
          <Typography variant="body2">
            <strong>Жами тўланган:</strong> {formatMoney(paidAmount ? paidAmount : "0")} Сумм
          </Typography>
          {overpaidAmount > 0 && (
            <Typography variant="body2" color="success">
              <strong>Ортиқча тўлўв:</strong> {formatMoney(overpaidAmount ? overpaidAmount : "0")} Сумм
            </Typography>
          )}
          <Typography variant="body2" color={remainingAmount > 0 ? 'error' : 'success'}>
            <strong>{remainingAmount > 0 ? "Тўланмаган сумма: " : ""}</strong> {remainingAmount > 0 ? formatMoney(remainingAmount) + ' Сумм' : ''}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Чеклар:
        </Typography>

        {checkImages.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {checkImages.map((check, index) => (
              <Box key={index} sx={{ position: 'relative' }}>
                <img 
                  src={check.url} 
                  alt={`Чек ${index + 1}`} 
                  style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 1 }}
                />
                <IconButton
                  onClick={() => setCheckImages(checkImages.filter((_, i) => i !== index))}
                  size="small"
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    right: 0, 
                    backgroundColor: 'rgba(255,255,255,0.7)' 
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          disabled={isSubmitting}
          fullWidth
          sx={{ mb: 2 }}
        >
          Чек юклаш
          <input 
            type="file" 
            hidden 
            accept="image/*" 
            multiple
            onChange={handleCheckUpload}
          />
        </Button>
        <Divider sx={{ my: 2 }} />
        
        {/* Секция доставки */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <DeliveryDiningIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1">Етказиб бериш маълумотлари</Typography>
        </Box>
        
        <Grid>
          <Grid>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                label="Манзил"
                fullWidth
                margin="normal"
                size="small"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                disabled={isSubmitting}
              />
              <Button
                variant="outlined"
                startIcon={<LocationOnIcon />}
                onClick={() => {
                  setShowAddressList(true);
                  setAddressSearch('');
                }}
                disabled={isSubmitting || addresses.length === 0}
                sx={{ mt: 1 }}
                fullWidth
                size="small"
              >
                Адрес танлаш
              </Button>
            </Box>
          </Grid>
          
          
          <Grid item>
            <TextField
              label="П. Чиқарилиш вақти"
              type="datetime-local"
              fullWidth
              margin="normal"
              size="small"
              sx={{minWidth:250}}
              value={deliveryTime}
              onChange={(e) => setDeliveryTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting}
            />
            <TextField
              label="П. Йетиб бориш вақти"
              type="datetime-local"
              fullWidth
              margin="normal"
              size="small"
              sx={{minWidth:250}}
              value={actualDeliveryTime}
              onChange={(e) => setActualDeliveryTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        
        {/* Тойи */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <EventIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1">Тойи</Typography>
        </Box>
        
        <TextField
          label="Той санаси"
          type="datetime-local"
          fullWidth
          margin="normal"
          size="small"
          value={partyDate}
          onChange={(e) => setPartyDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={isSubmitting}
        />
        <TextField
          label="Той маълумотлари"
          fullWidth
          margin="normal"
          value={partyComment}
          onChange={(e) => setPartyComment(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={isSubmitting}
        />

        <Divider sx={{ my: 2 }} />
        
        {/* Отзыв клиента */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ReviewsIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1">Мижоз баҳоси</Typography>
        </Box>
        
        <TextField
          label="Мижоз баҳоси ва шарҳи"
          fullWidth
          multiline
          margin="normal"
          size="small"
          value={clientReview}
          onChange={(e) => setClientReview(e.target.value)}
          disabled={isSubmitting}
        />

        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          gap: 1, 
          mt: 3,
        }}>
          <Button 
            onClick={handleSubmit} 
            fullWidth 
            variant="contained"
            disabled={isSubmitting}
            size="small"
          >
            {isSubmitting ? 'Сақланияпти...' : 'Сақлаш'}
          </Button>
          <Button 
            onClick={() => onClose(false)} 
            fullWidth 
            disabled={isSubmitting}
            size="small"
          >
            Бекор қилиш
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}